// src/pages/msp-inventory/pages/reports/SkuDetailPage.jsx
import React, { useEffect, useState } from "react";
import { ChevronLeft, TrendingUp, DollarSign, Boxes, Copy, Check } from "lucide-react";
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import DateRangePicker, { rangeToParams } from "../../components/reports/DateRangePicker";
import KpiCard from "../../components/reports/KpiCard";
import { SortableTH, Pagination } from "../../components/reports/TableControls";
import { getSkuDetail } from "../../services/catalogService";
import { usePersistedState } from "../../hooks/usePersistedState";
import { useSortAndPaginate } from "../../hooks/useSortAndPaginate";
import { API_BASE } from "../../../../consts";
import authService from "../../../../services/AuthService";
import RefreshableSection from "../../components/reports/RefreshableSection";
import ExportCsvButton from "../../components/reports/ExportCsvButton";
import { downloadCsv } from "../../utils/exportCsv";
import LoadingGame from "../../components/reports/LoadingGame";

const api = (path, opts) => authService.authenticatedFetch(`${API_BASE}${path}`, opts);

const RANGE_LABELS = { "7d": "7 Days", "30d": "30 Days", "90d": "90 Days", "6m": "6 Months", "12m": "12 Months", "ytd": "YTD" };
const rangeLabel = (range) => range.key === "custom" ? `${range.start} → ${range.end}` : (RANGE_LABELS[range.key] || range.key);

const CopyableId = ({ value }) => {
    const [copied, setCopied] = useState(false);
    return (
        <button
            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
            className="inline-flex items-center gap-1 font-mono text-gray-500 hover:text-teal-600 transition"
            title="Copy PO ID"
        >
            {value} {copied ? <Check size={11} className="text-green-500" /> : <Copy size={11} />}
        </button>
    );
};

const CopyIconButton = ({ value, title = "Copy" }) => {
    const [copied, setCopied] = useState(false);
    if (!value) return null;
    return (
        <button
            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
            className="inline-flex items-center text-gray-400 hover:text-teal-600 transition"
            title={title}
        >
            {copied ? <Check size={11} className="text-green-500" /> : <Copy size={11} />}
        </button>
    );
};

const SkuDetailPage = ({ catalogItemId, onBack }) => {
    // Persisted globally (not per-SKU) — landing on any item's detail page
    // keeps whatever range/sort you were last using.
    const [range, setRange] = usePersistedState("reports:sku-detail:range", { key: "30d" });
    const [ordersMetric, setOrdersMetricState] = usePersistedState("reports:sku-detail:orders-metric", { value: "units" });
    const setOrdersMetric = (v) => setOrdersMetricState({ value: v });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false); // true only while re-fetching over EXISTING data (range change) — distinct from the initial full-page loading state
    const [error, setError] = useState("");

    useEffect(() => {
        const isInitialLoad = !data;
        if (isInitialLoad) setLoading(true);
        else setRefreshing(true);
        setError("");

        getSkuDetail(catalogItemId, rangeToParams(range))
            .then(setData)
            .catch((e) => setError(e.message || "Failed to load"))
            .finally(() => {
                setLoading(false);
                setRefreshing(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogItemId, range]);

    // Backfill missing image the same way every other report does.
    useEffect(() => {
        if (!data?.item) return;
        // "" means already tried + confirmed no image — skip re-fetching.
        // Only null/undefined (never attempted) should trigger a lookup.
        if (data.item.image_url || data.item.image_url === "") return;
        api(`/api/walmart/api/item-details?catalog_item_id=${encodeURIComponent(data.item.id)}`)
            .then((r) => r.json())
            .then((d) => {
                if (d?.image_url) {
                    setData((prev) => prev ? { ...prev, item: { ...prev.item, image_url: d.image_url, brand: d.brand || prev.item.brand } } : prev);
                } else if (d?.image_url === "") {
                    setData((prev) => prev ? { ...prev, item: { ...prev.item, image_url: "" } } : prev);
                }
            })
            .catch(() => { /* non-fatal */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.item?.id]);

    const { sorted, pageItems, page, setPage, totalPages, sortConfig, requestSort, pageSize } =
        useSortAndPaginate(data?.orders || [], { key: "order_date", dir: "desc" }, "reports:sku-detail:orders-table");

    if (loading && !data) {
        return <LoadingGame label="Loading item detail..." />;
    }
    if (error) {
        return <div className="text-center py-16 text-red-500 text-sm">{error}</div>;
    }
    if (!data) return null;

    const { item, unit_cost, unit_fees, fee_breakdown = {}, cost_breakdown, flags = [], custom_fields = [], gross_margin_per_unit,
        estimated_profit_per_unit, estimated_roi, daily_series,
        total_units_sold, total_revenue, total_cogs, total_platform_fees, net_profit,
        velocity_units_per_day, velocity_units_30d, days_of_stock_remaining, stock_snapshots, orders } = data;

    const handleExport = () => {
        const summaryLines = [
            ["SKU", item.sku], ["Title", item.title], ["Brand", item.brand || ""],
            ["Marketplace", item.marketplace || ""], ["Price", item.price ?? ""],
            ["SF Stock", item.sf_stock ?? ""], ["WFS Stock", item.wfs_stock ?? ""],
            ["Supplier", item.supplier || ""], ["Expiration Date", item.expiration_date || ""],
            ["Notes", item.notes || ""],
            ["Flags", flags.map((f) => f.name).join("; ")],
            ...cost_breakdown.map((c) => [`Cost: ${c.name}`, c.value]),
            ...custom_fields.map((f) => [`Field: ${f.name}`, f.value]),
            ["Total Unit Cost", unit_cost], ["Gross Margin / Unit (before fees)", gross_margin_per_unit],
            ["Est. Platform Fees / Unit", unit_fees], ["Est. Profit / Unit", estimated_profit_per_unit],
            ["Est. ROI %", estimated_roi ?? ""],
            ["", ""],
        ];
        downloadCsv(
            `sku_${item.sku}_${new Date().toISOString().slice(0, 10)}.csv`,
            ["Order Date", "PO ID", "Qty", "Total", "Status", "State"],
            (orders || []).map((o) => [new Date(o.order_date).toLocaleDateString(), o.purchase_order_id, o.quantity, Number(o.charge_total).toFixed(2), o.status, o.state]),
            summaryLines
        );
    };

    const label = rangeLabel(range);
    const hasStockHistory = stock_snapshots && stock_snapshots.length > 1;

    return (
        <div className="max-w-6xl mx-auto py-8 px-6">
            <div className="flex items-center gap-3 mb-6 flex-wrap">
                <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
                    <ChevronLeft size={20} />
                </button>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    {item.image_url ? (
                        <div className="relative group flex-shrink-0">
                            <img src={item.image_url} alt="" className="w-12 h-12 object-contain rounded-lg" />
                            <div className="hidden group-hover:block absolute z-30 w-56 h-56 top-16 bg-white border border-gray-200 rounded-xl shadow-xl p-2">
                                <img src={item.image_url} alt="" className="w-56 h-56 object-contain" />
                            </div>
                        </div>
                    ) : (
                        <span className="w-12 h-12 flex items-center justify-center text-2xl flex-shrink-0">📦</span>
                    )}
                    <div className="min-w-0">
                        <a
                            href={`https://www.walmart.com/ip/${item.wpid}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={item.title || item.sku}
                            className="text-lg font-bold text-gray-800 truncate hover:text-teal-600 hover:underline transition block"
                        >
                            {item.title || item.sku}
                        </a>
                        <p className="text-xs text-gray-400 font-mono flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1">
                                {item.sku}
                                <CopyIconButton value={item.sku} title="Copy SKU" />
                            </span><br/>
                            {item.upc && (
                                <span className="inline-flex items-center gap-1">
                                    UPC {item.upc}
                                    <CopyIconButton value={item.upc} title="Copy UPC" />
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <ExportCsvButton onExport={handleExport} label="Export SKU Report" />
                <DateRangePicker range={range} setRange={setRange} />
            </div>

            <RefreshableSection refreshing={refreshing}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <KpiCard label={`Units Sold (${label})`} value={total_units_sold} sublabel={`${velocity_units_per_day}/day avg in range`} color="blue" />
                    <KpiCard label={`Revenue (${label})`} value={`$${total_revenue.toFixed(2)}`} color="teal" />
                    <KpiCard label={`Net Profit (${label})`} value={`$${net_profit.toFixed(2)}`} sublabel={`COGS $${total_cogs.toFixed(2)} · Fees $${total_platform_fees.toFixed(2)}`} color={net_profit >= 0 ? "green" : "red"} />
                    <KpiCard
                        label="Stock Remaining"
                        value={days_of_stock_remaining != null ? `${days_of_stock_remaining}d` : "—"}
                        sublabel={`SF ${item.sf_stock ?? 0} · WFS ${item.wfs_stock ?? 0} · based on last 30d (${velocity_units_30d} sold)`}
                        color={days_of_stock_remaining != null && days_of_stock_remaining < 14 ? "red" : "purple"}
                    />
                </div>
            </RefreshableSection>

            <div className="grid md:grid-cols-3 gap-6 mb-6">
                <RefreshableSection refreshing={refreshing} className="md:col-span-2">
                    <div className="bg-white rounded-2xl shadow-lg p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-gray-800 text-sm flex items-center gap-2">
                                <TrendingUp size={16} className="text-teal-600" /> Orders — {label}
                            </h3>
                            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                                <button onClick={() => setOrdersMetric("units")}
                                    className={`px-3 py-1 text-xs font-bold rounded-md transition ${ordersMetric.value === "units" ? "bg-white shadow text-teal-700" : "text-gray-500"}`}>
                                    Units
                                </button>
                                <button onClick={() => setOrdersMetric("revenue")}
                                    className={`px-3 py-1 text-xs font-bold rounded-md transition ${ordersMetric.value === "revenue" ? "bg-white shadow text-teal-700" : "text-gray-500"}`}>
                                    Revenue
                                </button>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={daily_series}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(v) => ordersMetric.value === "revenue" ? `$${Number(v).toFixed(2)}` : v} />
                                {ordersMetric.value === "units" ? (
                                    <Bar dataKey="units" fill="#0d9488" name="Units" radius={[3, 3, 0, 0]} />
                                ) : (
                                    <Bar dataKey="revenue" fill="#2563eb" name="Revenue ($)" radius={[3, 3, 0, 0]} />
                                )}
                            </BarChart>
                        </ResponsiveContainer>

                        {hasStockHistory ? (
                            <div className="mt-6 pt-5 border-t border-gray-100">
                                <h4 className="font-bold text-gray-700 text-xs mb-3">Stock Over Time</h4>
                                <ResponsiveContainer width="100%" height={160}>
                                    <LineChart data={stock_snapshots}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                        <XAxis dataKey="snapshot_date" tick={{ fontSize: 10 }} />
                                        <YAxis tick={{ fontSize: 10 }} />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Line type="stepAfter" dataKey="sf_stock" stroke="#f97316" strokeWidth={2} dot={false} name="SF Stock" />
                                        <Line type="stepAfter" dataKey="wfs_stock" stroke="#9333ea" strokeWidth={2} dot={false} name="WFS Stock" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="mt-6 pt-5 border-t border-gray-100 text-center text-[11px] text-gray-400">
                                Stock-over-time will appear here once a few days of inventory syncs have been recorded.
                            </div>
                        )}
                    </div>
                </RefreshableSection>

                <RefreshableSection refreshing={refreshing}>
                    <div className="bg-white rounded-2xl shadow-lg p-5 mb-6">
                        <h3 className="font-bold text-gray-800 text-sm mb-4 flex items-center gap-2">
                            <DollarSign size={16} className="text-purple-600" /> Unit Economics
                        </h3>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between"><span className="text-gray-500">Sell Price</span><span className="font-semibold">${(item.price || 0).toFixed(2)}</span></div>
                            {cost_breakdown.map((c) => (
                                <div key={c.name} className="flex justify-between"><span className="text-gray-500">{c.name}</span><span className="font-semibold text-red-500">-${c.value.toFixed(2)}</span></div>
                            ))}
                            <div className="border-t border-gray-100 pt-2 flex justify-between"><span className="text-gray-700 font-bold">Total Cost</span><span className="font-bold">${unit_cost.toFixed(2)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-700 font-bold">Margin / Unit (before fees)</span><span className={`font-bold ${gross_margin_per_unit >= 0 ? "text-green-600" : "text-red-600"}`}>${gross_margin_per_unit.toFixed(2)}</span></div>
                            <div className="border-t border-gray-100 pt-2 relative group cursor-help">
                                <div className="flex justify-between">
                                    <span className="text-gray-500 flex items-center gap-1">Est. Platform Fees <span className="underline decoration-dotted text-[10px]">(hover)</span></span>
                                    <span className="font-semibold text-red-500">-${(unit_fees || 0).toFixed(2)}</span>
                                </div>
                                {Object.keys(fee_breakdown).length > 0 && (
                                    <div className="hidden group-hover:block absolute z-20 bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-56 text-left">
                                        <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Fee Breakdown / Unit</p>
                                        <div className="space-y-1 text-xs">
                                            {Object.entries(fee_breakdown).map(([k, v]) => v ? (
                                                <div key={k} className="flex justify-between">
                                                    <span className="text-gray-500">{k.replace(/_/g, " ")}</span>
                                                    <span className="font-semibold text-gray-700">${Number(v).toFixed(2)}</span>
                                                </div>
                                            ) : null)}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-700 font-bold">Est. Profit / Unit</span>
                                <span className={`font-bold ${estimated_profit_per_unit >= 0 ? "text-green-600" : "text-red-600"}`}>
                                    ${estimated_profit_per_unit.toFixed(2)}{estimated_roi != null && <span className="text-[10px] text-purple-600 ml-1">({estimated_roi.toFixed(1)}% ROI)</span>}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-lg p-5">
                        <h3 className="font-bold text-gray-800 text-sm mb-4">Custom Data</h3>
                        <div className="space-y-3 text-xs">
                            <div>
                                <p className="text-gray-400 font-semibold mb-1">Supplier / Expiration</p>
                                <p className="text-gray-700">{item.supplier || "—"} {item.expiration_date ? `· exp. ${item.expiration_date}` : ""}</p>
                            </div>
                            <div>
                                <p className="text-gray-400 font-semibold mb-1">Flags</p>
                                {flags.length === 0 ? <p className="text-gray-300">None</p> : (
                                    <div className="flex flex-wrap gap-1.5">
                                        {flags.map((f) => (
                                            <span key={f.name} style={{ backgroundColor: `${f.color}22`, color: f.color }}
                                                className="font-bold px-2 py-0.5 rounded-full">{f.name}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <p className="text-gray-400 font-semibold mb-1">Custom Fields</p>
                                {custom_fields.length === 0 ? <p className="text-gray-300">None set</p> : (
                                    custom_fields.map((f) => (
                                        <div key={f.name} className="flex justify-between border-b border-gray-50 py-1">
                                            <span className="text-gray-500">{f.name}</span><span className="font-semibold text-gray-700">{f.value}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                            {item.notes && (
                                <div>
                                    <p className="text-gray-400 font-semibold mb-1">Notes</p>
                                    <p className="text-gray-700 whitespace-pre-wrap">{item.notes}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </RefreshableSection>
            </div>

            <RefreshableSection refreshing={refreshing}>
                <div className="bg-white rounded-2xl shadow-lg p-5">
                    <h3 className="font-bold text-gray-800 text-sm mb-4 flex items-center gap-2">
                        <Boxes size={16} className="text-blue-600" /> Orders — {label} ({sorted.length})
                    </h3>
                    <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 z-10 bg-white">
                                <tr className="text-left text-gray-400 border-b border-gray-100">
                                    <SortableTH label="Order Date" sortKey="order_date" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableTH label="PO ID" sortKey="purchase_order_id" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableTH label="Qty" sortKey="quantity" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableTH label="Total" sortKey="charge_total" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableTH label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableTH label="State" sortKey="state" sortConfig={sortConfig} onSort={requestSort} />
                                </tr>
                            </thead>
                            <tbody>
                                {pageItems.map((o) => (
                                    <tr key={o.id} className="border-b border-gray-50">
                                        <td className="py-2">{new Date(o.order_date).toLocaleDateString()}</td>
                                        <td className="py-2"><CopyableId value={o.purchase_order_id} /></td>
                                        <td className="py-2">{o.quantity}</td>
                                        <td className="py-2">${Number(o.charge_total).toFixed(2)}</td>
                                        <td className="py-2">
                                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${o.status === "Delivered" ? "bg-green-50 text-green-600" : o.status === "Cancelled" ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"}`}>
                                                {o.status}
                                            </span>
                                        </td>
                                        <td className="py-2">{o.state}</td>
                                    </tr>
                                ))}
                                {pageItems.length === 0 && (
                                    <tr><td colSpan={6} className="py-8 text-center text-gray-400">No orders in this range</td></tr>
                                )}
                            </tbody>
                        </table>
                        <Pagination page={page} totalPages={totalPages} setPage={setPage} totalItems={sorted.length} pageSize={pageSize} />
                    </div>
                </div>
            </RefreshableSection>
        </div>
    );
};

export default SkuDetailPage;